import type { IResponse } from '@/features/api/extranet'
import type { DemandeDTO, ModelDemande } from '@/features/api/extranet/dto/Demande/shared'
import {
  TypeDocumentTemplateUrl,
  type TypeDocumentTemplate,
} from '@/features/domaine/types/opco/shared/TypeDocumentModele'
import { ensureSuccessStatusCode, useFetch } from '@/features/fetch'
import { locationToEndPointsDemande } from '@/features/services'
import type { KeyObject } from '@/features/shared/tools/KeyObjectStore'
import type { NavigateToLocation } from '@/router/navigateTo'
import demandeServices from './DemandeAllService'

export type BuildServiceDemandeOptions<TModelDemande extends ModelDemande> = {
  location: KeyObject<NavigateToLocation>
  normaliserData?: (demande: DemandeDTO<TModelDemande>) => DemandeDTO<TModelDemande>
  loadExtraData?: (demande: DemandeDTO<TModelDemande>) => Promise<DemandeDTO<TModelDemande>>
}

const INIT_DEFAULT: RequestInit = { sablier: true, dialogMode: 'retry' } as const
const INIT_DEFAULT_POST: RequestInit = { ...INIT_DEFAULT, reloadPageOnConcurrencyException: true } as const

export function buildDemandeService<TModelDemande extends ModelDemande, TLov extends object>(
  options: BuildServiceDemandeOptions<TModelDemande>,
) {
  type TDemande = DemandeDTO<TModelDemande>
  const uriDemande = locationToEndPointsDemande(options.location).endPointEntite

  async function loadDemande(id: number, init?: RequestInit) {
    init = {
      ...INIT_DEFAULT,
      ...init,
    }

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`${uriDemande}/${id}`, init)
    ensureSuccessStatusCode(response)
    const result = (await response.json()) as IResponse<TDemande>

    // charger les données en extra si besoin
    if (options.loadExtraData) {
      result.data = await options.loadExtraData(result.data)
    }

    // normaliser data au besoin
    return options.normaliserData?.call(null, result.data) ?? result.data
  }

  async function saveDemande(data: TDemande, init?: RequestInit) {
    if (!init) init = INIT_DEFAULT_POST

    const request: RequestInit = {
      ...init,
      method: 'PUT',
      body: JSON.stringify(data),
    }

    // Stocke la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`${uriDemande}/${data.id}`, request)
    ensureSuccessStatusCode(response)
    const result = (await response.json()) as IResponse<TDemande>

    // charger les données en extra si besoin
    if (options.loadExtraData) {
      result.data = await options.loadExtraData(result.data)
    }

    // normaliser data au besoin
    return options.normaliserData?.call(null, result.data) ?? result.data
  }

  function loadLov(init?: RequestInit) {
    return demandeServices.loadLovWithLocation<TLov>(options.location, init)
  }

  async function downloadDocumentTemplate(
    id: number,
    typeDocumentTemplate: TypeDocumentTemplate,
    init?: RequestInit,
  ): Promise<Blob> {
    if (!init) init = { sablier: true, dialogMode: 'retry' }

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`${uriDemande}/${id}${TypeDocumentTemplateUrl[typeDocumentTemplate]}`, init)
    ensureSuccessStatusCode(response)

    return await response.blob()
  }
  /*
  async function downloadCerfa(id: number, init?: RequestInit): Promise<Blob> {
    if (!init) init = { sablier: true, dialogMode: 'retry' }

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`${uriDemande}/${id}/cerfa`, init)
    ensureSuccessStatusCode(response)

    return await response.blob()
  }

  async function downloadContratType(id: number, init?: RequestInit): Promise<Blob> {
    if (!init) init = { sablier: true, dialogMode: 'retry' }

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`${uriDemande}/${id}/contrattype`, init)
    ensureSuccessStatusCode(response)

    return await response.blob()
  }

  async function downloadLettreMission(id: number, init?: RequestInit): Promise<Blob> {
    if (!init) init = { sablier: true, dialogMode: 'retry' }

    // Charge la demande
    const fetch = useFetch('extranet')
    const response = await fetch(`${uriDemande}/${id}/lettremission`, init)
    ensureSuccessStatusCode(response)

    return await response.blob()
  }
  */
  return {
    loadDemande,
    saveDemande,
    loadLov,
    downloadDocumentTemplate,
    // downloadCerfa,
    // downloadContratType,
    // downloadLettreMission,
  }
}
